import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';

import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { DEFAULT_ROWS_PER_PAGE } from '../../../shared/constants/table.constants';
import { MIN_SEARCH_LENGTH } from '../../../shared/constants/search.constants';
import { useAdminGetUsers } from '../../hooks/admin-get-users.hook';
import { ADMIN_USERS_APP_PATHS } from '../../constants/users-paths.constants';
import { UserStatusFilter } from '../../constants/user-status-filter.constants';
import { User, UserSortColumn, UserSortDirection } from '../../types/user.type';
import { ALL_ROLES, RoleFilter, UsersListFilters } from '../../components/Containers/UsersListFilters.component';
import { UsersTable } from '../../components/Tables/UsersTable.component';
import CreateUserDialog from '../../components/Modals/CreateUserDialog.component';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },

  createAction: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
  },
});

export default function AdminUsersListView() {
  const classes = useStyles();
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>(ALL_ROLES);
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>('active');
  const [sortBy, setSortBy] = useState<UserSortColumn>('lastname');
  const [sortDirection, setSortDirection] = useState<UserSortDirection>('asc');

  const debouncedSearch = useDebounce(search, 400).trim();

  const { isLoading, isRefetching, totalCount, users } = useAdminGetUsers({
    page: page + 1,
    limit: rowsPerPage,
    sortBy,
    sortDirection,
    filters: {
      status: statusFilter,
      ...(roleFilter !== ALL_ROLES ? { role: roleFilter } : {}),
      ...(debouncedSearch.length >= MIN_SEARCH_LENGTH ? { search: debouncedSearch } : {}),
    },
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleRoleFilterChange = (value: RoleFilter) => {
    setRoleFilter(value);
    setPage(0);
  };

  const handleStatusFilterChange = (value: UserStatusFilter) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleSortChange = (column: UserSortColumn) => {
    setSortDirection(column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc');
    setSortBy(column);
    setPage(0);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleUserCreated = (user: User) => history.push(ADMIN_USERS_APP_PATHS.USER_DETAILS(user.id));

  return (
    <section className={classes.root}>
      <h2>Utilisateurs</h2>
      <div className={classes.toolbar}>
        <UsersListFilters
          search={search}
          roleFilter={roleFilter}
          statusFilter={statusFilter}
          onSearchChange={handleSearchChange}
          onRoleFilterChange={handleRoleFilterChange}
          onStatusFilterChange={handleStatusFilterChange}
        />
        <div className={classes.createAction}>
          <CreateUserDialog onCreated={handleUserCreated} />
        </div>
      </div>
      <UsersTable
        users={users}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={isLoading || isRefetching}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={handleSortChange}
      />
    </section>
  );
}
